'use client';

import { useEffect, useState, useRef } from 'react';
import { API_BASE } from '../lib/api';
import { useTranslation } from '../lib/i18n';

const RECONNECT_DELAY = 3000;
const RECONNECT_MAX_DELAY = 60000;
const WATCHDOG_TIMEOUT = 15000;
const POLL_FALLBACK_INTERVAL = 10000;
const FETCH_TIMEOUT = 5000;
const FILE_FLAG_POLL_INTERVAL = 30000; // poll /api/maintenance-flag every 30s

export default function BackendHealth({ active }: { active: boolean }) {
  const { t } = useTranslation();
  const [down, setDown] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [fileMaintenance, setFileMaintenance] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);
  const [offline, setOffline] = useState(false);
  const evtSourceRef = useRef<EventSource | null>(null);
  const lastEventTimeRef = useRef<number>(Date.now());
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const activeRef = useRef(active);
  activeRef.current = active;

  // Browser-level connectivity — distinguishes "no internet" from "backend down".
  // navigator.onLine is a heuristic (true = has a network interface, not a guarantee
  // the server is reachable), so it only affects which message is shown.
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    setOffline(!navigator.onLine);
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  // Poll file-based maintenance flag — works even when backend is down.
  // Deploy script creates maintenance.flag before stopping backend and removes
  // it after backend is healthy again. This lets us distinguish planned deploys
  // (show "Krátká přestávka") from real outages (show "Výpadek spojení").
  // Only poll file flag when SSE is NOT connected — SSE already reports maintenance.
  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    async function checkFileFlag() {
      if (sseConnected) return; // SSE handles maintenance reporting
      try {
        const res = await fetch('/api/maintenance-flag', { cache: 'no-store' });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setFileMaintenance(data.maintenance === true);
        }
      } catch { /* ignore — frontend itself may be down */ }
    }

    checkFileFlag();
    const interval = setInterval(checkFileFlag, FILE_FLAG_POLL_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [active, sseConnected]);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;

    async function checkBackendUp() {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
      try {
        const res = await fetch(`${API_BASE}/actuator/health`, { signal: controller.signal });
        clearTimeout(timer);
        if (cancelled) return;
        if (res.ok) {
          lastEventTimeRef.current = Date.now();
          setDown(false);
        } else {
          setDown(!fileMaintenance);
        }
      } catch {
        clearTimeout(timer);
        if (!cancelled) setDown(!fileMaintenance);
      }
    }

    function connect() {
      if (cancelled || !activeRef.current) return;

      if (evtSourceRef.current) {
        evtSourceRef.current.close();
        evtSourceRef.current = null;
      }

      const es = new EventSource(`${API_BASE}/api/health-sse`);
      evtSourceRef.current = es;
      lastEventTimeRef.current = Date.now();

      es.addEventListener('health', (event) => {
        lastEventTimeRef.current = Date.now();
        reconnectAttemptsRef.current = 0; // reset backoff on successful event
        setSseConnected(true);
        try {
          const data = JSON.parse((event as MessageEvent).data);
          if (data.maintenance === true) {
            setMaintenance(true);
            setDown(false);
            return;
          }
        } catch { /* ignore parse errors */ }
        setMaintenance(false);
        setDown(false);
      });

      es.onerror = () => {
        setSseConnected(false);
        // SSE drop ≠ backend down — on flaky networks (mobile data, proxies)
        // streams get killed every ~15s while regular requests work fine.
        // Show "down" only when the plain health check fails too.
        checkBackendUp();
        if (es.readyState === EventSource.CLOSED) {
          es.close();
          evtSourceRef.current = null;
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          // Exponential backoff: 3s, 6s, 12s, 24s, 48s, 60s (cap)
          const delay = Math.min(RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current), RECONNECT_MAX_DELAY);
          reconnectAttemptsRef.current++;
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      };
    }

    connect();

    // Watchdog: if no SSE event within 15s, try polling fallback
    const watchdog = setInterval(async () => {
      const elapsed = Date.now() - lastEventTimeRef.current;
      if (elapsed <= WATCHDOG_TIMEOUT) return;

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

      try {
        const res = await fetch(`${API_BASE}/actuator/health`, {
          signal: controller.signal,
        });
        clearTimeout(timer);
        if (cancelled) return;
        if (res.ok) {
          lastEventTimeRef.current = Date.now();
          setDown(false);
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          reconnectAttemptsRef.current = 0; // reset backoff — backend is reachable
          reconnectTimerRef.current = setTimeout(connect, RECONNECT_DELAY);
        } else {
          setDown(!fileMaintenance);
        }
      } catch {
        clearTimeout(timer);
        if (cancelled) return;
        setDown(!fileMaintenance);
        if (evtSourceRef.current && evtSourceRef.current.readyState !== EventSource.OPEN) {
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          const delay = Math.min(RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current), RECONNECT_MAX_DELAY);
          reconnectAttemptsRef.current++;
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      }
    }, POLL_FALLBACK_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(watchdog);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (evtSourceRef.current) {
        evtSourceRef.current.close();
        evtSourceRef.current = null;
      }
    };
  }, [active, fileMaintenance]);

  // File flag takes priority — planned deploy shows maintenance, not outage
  const showMaintenance = maintenance || fileMaintenance;
  if (!down && !showMaintenance && !offline) return null;

  const state = offline
    ? {
        icon: '📶',
        titleKey: 'backendHealthOffline',
        descKey: 'backendHealthOfflineDesc',
        statusKey: 'backendHealthWaitingForNetwork',
        border: 'border-wood',
        titleColor: 'text-bark',
      }
    : showMaintenance
      ? {
          icon: '🔧',
          titleKey: 'backendHealthMaintenance',
          descKey: 'backendHealthMaintenanceDesc',
          statusKey: 'backendHealthReconnecting',
          border: 'border-ochre',
          titleColor: 'text-ochre',
        }
      : {
          icon: '📡',
          titleKey: 'backendHealthDown',
          descKey: 'backendHealthUnavailable',
          statusKey: 'backendHealthReconnecting',
          border: 'border-rust',
          titleColor: 'text-rust',
        };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-bark/60 backdrop-blur-sm p-4">
      <div
        className={`bg-linen border ${state.border} rounded-rustic shadow-xl p-5 sm:p-8 w-full max-w-md max-h-[90dvh] overflow-y-auto text-center`}
        role="alert"
        aria-live="assertive"
      >
        <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">{state.icon}</div>
        <h2 className={`text-lg sm:text-xl font-bold ${state.titleColor} mb-2`}>{t(state.titleKey)}</h2>
        <p className="text-sm text-wood mb-4">
          {t(state.descKey)}
        </p>
        <div className="flex items-center justify-center gap-2 text-wood">
          <div className="w-3 h-3 border-2 border-wood border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <span className="text-xs">{t(state.statusKey)}</span>
        </div>
      </div>
    </div>
  );
}
